import { isNil, isUndefined, last } from 'lodash';
import { PickupDropoffOption, Task, TaskStatus, TaskTypeId, WayPoint } from '@bringg/types';

export const isValidLatLng = (lat: number | undefined, lng: number | undefined): boolean =>
	lat && lng && lat > -90 && lat < 90 && lng > -180 && lng < 180;

export const getValidWaypointsLatLng = (way_points: WayPoint[]): WayPoint[] =>
	way_points.map(wp => {
		if (isValidLatLng(wp.lat, wp.lng)) return wp;
		return { ...wp, lat: 0, lng: 0 };
	});

const TASK_CLOSED_STATUSES = new Set([TaskStatus.Rejected, TaskStatus.Done, TaskStatus.Cancelled]);
export const isOpenTask = (task: Task) => {
	if (!task) {
		return false;
	}
	return !isNil(task.invalidated) && task.ready_to_execute && !TASK_CLOSED_STATUSES.has(task.status);
};

export const isPlanningTask = (task: Task) => {
	if (!task) {
		return false;
	}
	// according to ruby api there also should be not_ready_to_execute_and_invisible === false
	// but this field is rarely present in task response structure
	return !isNil(task.invalidated) && task.ready_to_execute === false && !TASK_CLOSED_STATUSES.has(task.status);
};

const getCustomerPickupDropoffOption = (taskTypeId: Task['task_type_id']) => {
	switch (taskTypeId) {
		case TaskTypeId.PICKUP:
			return PickupDropoffOption.PICKUP;
		case TaskTypeId.DELIVERY:
			return PickupDropoffOption.DROPOFF;
		case TaskTypeId.PICKUP_AND_DELIVERY:
			return PickupDropoffOption.BOTH;
		default:
			return undefined;
	}
};

const getActionableWayPointByTaskType = (taskTypeId: Task['task_type_id'], visibleWayPoints: Task['way_points']) => {
	if (!visibleWayPoints?.length) return null;

	const targetPickupDropoff = getCustomerPickupDropoffOption(taskTypeId);

	return isUndefined(targetPickupDropoff)
		? null
		: visibleWayPoints.find(wp => wp.pickup_dropoff_option === targetPickupDropoff);
};

export const getActionableWaypoint = (task: Pick<Task, 'task_type_id' | 'way_points'>): WayPoint => {
	const { task_type_id, way_points } = task;

	if (!way_points?.length) return null;

	const visibleWaypoints = way_points.filter(wp => !wp.hidden);

	return getActionableWayPointByTaskType(task_type_id, visibleWaypoints) || last(visibleWaypoints);
};

export const TASK_TYPE_TRANSLATIONS = {
	[TaskTypeId.PICKUP]: 'TASK_TYPE_NAMES.PICKUP',
	[TaskTypeId.RETURN_TASK]: 'TASK_TYPE_NAMES.RETURN',
	[TaskTypeId.DELIVERY]: 'TASK_TYPE_NAMES.DELIVERY',
	[TaskTypeId.PICKUP_AND_DELIVERY]: 'TASK_TYPE_NAMES.PICKUP_DELIVERY',
	[TaskTypeId.CLICK_AND_COLLECT]: 'TASK_TYPE_NAMES.CONSUMER_COLLECTION',
	[TaskTypeId.SERVICE]: 'TASK_TYPE_NAMES.SERVICE',
	UNDEFINED: 'TASK_TYPE_NAMES.UNDEFINED'
};
